import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtBRL, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";

const emptyPlan = {
  name: "", slug: "", description: "",
  cpu_cores: 1, ram_gb: 1, ssd_gb: 20, bandwidth_tb: 1,
  location: "São Paulo", location_code: "BR", network_gbps: 10, price_brl: 29.9,
  billing_cycle: "monthly", is_featured: false, is_active: true,
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null); // null (closed), "new" or plan_id
  const [form, setForm] = useState(emptyPlan);

  const load = () => api.get("/admin/plans/all").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyPlan); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p.plan_id); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        cpu_cores: Number(form.cpu_cores),
        ram_gb: Number(form.ram_gb),
        ssd_gb: Number(form.ssd_gb),
        bandwidth_tb: Number(form.bandwidth_tb),
        network_gbps: Number(form.network_gbps),
        price_brl: Number(form.price_brl),
        portal_package_id: form.portal_package_id ? Number(form.portal_package_id) : null,
      };
      if (editing === "new") await api.post("/admin/plans", payload);
      else await api.put(`/admin/plans/${editing}`, payload);
      toast.success("Plano salvo");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir plano?")) return;
    try { await api.delete(`/admin/plans/${id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// admin · planos</div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Planos VPS</h1>
        </div>
        <Button onClick={openNew} className="rounded-sm bg-white text-black hover:bg-zinc-200 btn-sharp" data-testid="admin-new-plan-btn"><Plus className="h-4 w-4 mr-2" /> Novo plano</Button>
      </div>

      <div className="border border-white/10 bg-[#121214]">
        <table className="w-full text-sm">
          <thead className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            <tr className="border-b border-white/10">
              <th className="text-left px-5 py-3">Nome</th>
              <th className="text-left px-5 py-3">Slug</th>
              <th className="text-left px-5 py-3">Specs</th>
              <th className="text-left px-5 py-3">Local</th>
              <th className="text-left px-5 py-3">Preço</th>
              <th className="text-left px-5 py-3">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.plan_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-plan-row-${p.slug}`}>
                <td className="px-5 py-3">{p.name}{p.is_featured && <Badge className="ml-2 rounded-sm bg-white text-black">★</Badge>}</td>
                <td className="px-5 py-3 font-mono text-xs">{p.slug}</td>
                <td className="px-5 py-3 font-mono text-xs">{p.cpu_cores}v · {p.ram_gb}G · {p.ssd_gb}G · {p.bandwidth_tb ? `${p.bandwidth_tb}TB` : "∞"} · {p.network_gbps || 1} Gbps</td>
                <td className="px-5 py-3">{p.location} · {p.location_code === "BR" ? "Brasil" : "Paris"}</td>
                <td className="px-5 py-3 font-mono">{fmtBRL(p.price_brl)}</td>
                <td className="px-5 py-3">{p.is_active ? <span className="text-emerald-400 text-xs font-mono">ATIVO</span> : <span className="text-zinc-500 text-xs font-mono">INATIVO</span>}</td>
                <td className="px-5 py-3 text-right space-x-2">
                  <button onClick={() => openEdit(p)} className="text-zinc-400 hover:text-white" data-testid={`edit-plan-${p.slug}`}><Pencil className="h-4 w-4 inline" /></button>
                  <button onClick={() => remove(p.plan_id)} className="text-zinc-400 hover:text-red-400" data-testid={`delete-plan-${p.slug}`}><Trash2 className="h-4 w-4 inline" /></button>
                </td>
              </tr>
            ))}
            {plans.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-zinc-500">Sem planos cadastrados.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm bg-[#121214] border-white/10 max-w-2xl" data-testid="plan-form-dialog">
          <DialogHeader><DialogTitle className="font-heading tracking-tighter">{editing === "new" ? "Novo plano" : "Editar plano"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2"><Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-sm bg-[#09090B] border-white/10 mt-1" data-testid="plan-form-name" /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono" data-testid="plan-form-slug" /></div>
            <div>
              <Label htmlFor="plan-region" className="text-xs font-mono uppercase tracking-widest text-zinc-400">Região de entrega</Label>
              <select id="plan-region" value={form.location_code || "PAR"} onChange={(e) => setForm({ ...form, location_code: e.target.value, location: e.target.value === "BR" ? "São Paulo" : "Paris", network_gbps: e.target.value === "BR" ? 10 : 1 })} required className="w-full h-10 px-3 rounded-sm bg-[#09090B] border border-white/10 mt-1" data-testid="plan-form-region">
                <option value="BR">Brasil (Proxmox BR)</option>
                <option value="PAR">França (Paris)</option>
              </select>
            </div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Localização</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required className="rounded-sm bg-[#09090B] border-white/10 mt-1" /></div>
            <div className="col-span-2"><Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm bg-[#09090B] border-white/10 mt-1" rows={2} /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">vCPU</Label><Input type="number" min={1} value={form.cpu_cores} onChange={(e) => setForm({ ...form, cpu_cores: e.target.value })} className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono" /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">RAM (GB)</Label><Input type="number" min={1} value={form.ram_gb} onChange={(e) => setForm({ ...form, ram_gb: e.target.value })} className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono" /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">SSD (GB)</Label><Input type="number" min={1} value={form.ssd_gb} onChange={(e) => setForm({ ...form, ssd_gb: e.target.value })} className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono" /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Tráfego (TB, 0=ilimitado)</Label><Input type="number" min={0} value={form.bandwidth_tb} onChange={(e) => setForm({ ...form, bandwidth_tb: e.target.value })} className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono" /></div>
            <div>
              <Label htmlFor="plan-network" className="text-xs font-mono uppercase tracking-widest text-zinc-400">Porta de rede</Label>
              <select id="plan-network" value={form.network_gbps || 1} onChange={(e) => setForm({ ...form, network_gbps: Number(e.target.value) })} className="w-full h-10 px-3 rounded-sm bg-[#09090B] border border-white/10 mt-1" data-testid="plan-form-network">
                <option value={1}>1 Gbps</option>
                <option value={10}>10 Gbps</option>
              </select>
            </div>
            <div className="col-span-2"><Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Preço (BRL/mês)</Label><Input type="number" step="0.01" min={0} value={form.price_brl} onChange={(e) => setForm({ ...form, price_brl: e.target.value })} className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono" data-testid="plan-form-price" /></div>
            <div className="col-span-2">
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Portal Package ID (Flix VPS · opcional)</Label>
              <Input type="number" min={0} value={form.portal_package_id ?? ""} onChange={(e) => setForm({ ...form, portal_package_id: e.target.value })} className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono" placeholder="Deixe em branco para não vincular" data-testid="plan-form-portal-id" />
              <p className="text-xs text-zinc-500 mt-1">Se preenchido, cada VPS criada por esse plano usará este package_id do portal Flix.</p>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} /><span className="text-sm">Destaque</span></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><span className="text-sm">Ativo</span></div>
            <Button type="submit" className="col-span-2 rounded-sm bg-white text-black hover:bg-zinc-200 mt-2" data-testid="plan-form-submit">Salvar</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
